/**
 * useSourceCitations - verknuepft Quellenverweise [n] im Fliesstext mit der
 * Quellenliste einer Interaktion. Liefert die nummerierten Quellen, eine
 * cite()-Funktion fuer den Text und jumpToSource() zum Hinscrollen.
 *
 * Quelle der Liste: verknuepfte Eintraege aus interaction_sources; fuer alle
 * Nummern ohne Verknuepfung die Textzeilen aus content.quellen
 * ("[n] Autor (Jahr): Titel. https://...").
 */
import { useEffect, useRef, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { interactionSourcesQuery, type InteractionSource } from "@/lib/public-sources.functions";
import { CitedText } from "@/components/CitedText";
import { useT } from "@/i18n/LanguageContext";

export type NumberedSource =
  | { kind: "linked"; key: string; n: number; s: InteractionSource; note: string }
  | { kind: "text"; key: string; n: number; text: string; url: string | null };

const NUMBER_PREFIX = /^\s*\[(\d+)\]\s*/;
const URL_IN_TEXT = /https?:\/\/[^\s<>"]+/;

/** "[4] Strassman RJ (1994): Titel. https://doi.org/x." -> number, text without URL, URL */
export function parseQuelle(raw: string, index: number) {
  const m = raw.match(NUMBER_PREFIX);
  let rest = m ? raw.slice(m[0].length) : raw;
  let url: string | null = null;
  const u = rest.match(URL_IN_TEXT);
  if (u) {
    // Trailing sentence punctuation is not part of the URL.
    url = u[0].replace(/[.,;:)\]]+$/, "");
    rest = (rest.slice(0, u.index) + rest.slice((u.index ?? 0) + u[0].length)).trim();
  }
  return { n: m ? Number(m[1]) : index + 1, text: rest.replace(/[\s:,;([-]+$/, ""), url };
}

export function useSourceCitations(
  interactionId: number | null | undefined,
  lang: "de" | "en",
  quellen: string[] | null | undefined,
  /** Called before scrolling so the caller can expand a collapsed section. */
  onOpen?: () => void,
) {
  const t = useT();
  const [highlighted, setHighlighted] = useState<number | null>(null);
  const timers = useRef<number[]>([]);
  const { data } = useQuery(interactionSourcesQuery(interactionId ?? null));
  const domPrefix = `ic-src-${interactionId ?? "x"}`;

  useEffect(() => {
    const pending = timers.current;
    return () => pending.forEach((id) => window.clearTimeout(id));
  }, []);

  // Text sources keep their own numbering from content.quellen.
  const parsedText = (quellen ?? [])
    .map((raw, i) => ({ kind: "text" as const, key: `t-${i}`, ...parseQuelle(raw, i) }))
    .filter((e) => e.text || e.url);

  // A linked source's fixed number is language independent: prefix from note_de, else note_en.
  const prefixNumber = (note: string | null | undefined): number | null => {
    const m = note?.match(NUMBER_PREFIX);
    return m ? Number(m[1]) : null;
  };
  const stripPrefix = (note: string) => note.replace(NUMBER_PREFIX, "");
  const linkedRaw = (data ?? []).map((s) => ({
    s,
    fixed: prefixNumber(s.note_de) ?? prefixNumber(s.note_en),
  }));
  const fixedNumbers = new Set(
    linkedRaw.map((e) => e.fixed).filter((n): n is number => n != null),
  );
  // Linked sources without a fixed number continue after the highest known number.
  let next = Math.max(0, ...parsedText.map((e) => e.n), ...fixedNumbers) + 1;
  const linked: NumberedSource[] = linkedRaw.map(({ s, fixed }) => {
    const note = (lang === "en" ? (s.note_en ?? s.note_de) : (s.note_de ?? s.note_en)) ?? "";
    return {
      kind: "linked",
      key: `l-${s.id}`,
      n: fixed ?? next++,
      s,
      note: stripPrefix(note),
    };
  });
  // Hide a text source only when a linked source explicitly claims the same number.
  const textOnly: NumberedSource[] = parsedText.filter((e) => !fixedNumbers.has(e.n));
  const numbered = [...linked, ...textOnly].sort((a, b) => a.n - b.n);
  const known = new Set(numbered.map((e) => e.n));

  const jumpToSource = (n: number) => {
    onOpen?.();
    setHighlighted(n);
    timers.current.push(
      window.setTimeout(() => {
        const el = document.getElementById(`${domPrefix}-${n}`);
        if (!el) return;
        // Move keyboard navigation with the visual jump. The next Tab reaches
        // this source's link instead of returning to the citation in the text.
        el.focus({ preventScroll: true });
        const rm = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
        el.scrollIntoView({ behavior: rm ? "auto" : "smooth", block: "center" });
      }, 50),
      window.setTimeout(() => setHighlighted((cur) => (cur === n ? null : cur)), 2500),
    );
  };

  // Only numbers that have a source entry become buttons; others stay plain text.
  const cite = (text: string | null | undefined): ReactNode => (
    <CitedText
      text={text}
      onCite={known.size ? jumpToSource : undefined}
      isKnown={(n) => known.has(n)}
      label={(n) => t("ic.cite.show").replace("{n}", String(n))}
    />
  );

  return { numbered, domPrefix, highlighted, jumpToSource, cite };
}

